import { useState, useCallback, useEffect, useRef } from 'react';
import {
    initiateCall,
    endCall,
    getCallDetails,
    getTranscript,
    generateCallSummary,
    saveCallAsComment,
    getCallHistory
} from '../api/phone';

/**
 * Call status constants
 */
export const CALL_STATUS = {
    IDLE: 'idle',
    INITIATING: 'initiating',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    ON_HOLD: 'on_hold',
    ENDED: 'ended',
    FAILED: 'failed'
};

/**
 * Normalize phone number to E.164 format
 * @param {string} phone - Phone number in any format
 * @returns {string} Phone number in E.164 format (+1XXXXXXXXXX)
 */
function normalizePhoneToE164(phone) {
    if (!phone) return '';

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // If already has country code (11 digits starting with 1)
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
    }

    // If 10 digits (US number without country code), add +1
    if (digits.length === 10) {
        return `+1${digits}`;
    }

    // If already in E.164 format with +
    if (phone.startsWith('+')) {
        return phone;
    }

    // Fallback: return with + prefix if it looks valid
    if (digits.length >= 10) {
        return `+${digits}`;
    }

    // Return original if we can't normalize
    return phone;
}

/**
 * Hook for managing phone call state and operations
 *
 * @returns {Object} Phone call state and handlers
 */
export function usePhoneCall() {
    // Modal state
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);

    // Call state
    const [callStatus, setCallStatus] = useState(CALL_STATUS.IDLE);
    const [currentCall, setCurrentCall] = useState(null);
    const [callDuration, setCallDuration] = useState(0);
    const [error, setError] = useState(null);

    // Call controls state
    const [isMuted, setIsMuted] = useState(false);
    const [isOnHold, setIsOnHold] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [showKeypad, setShowKeypad] = useState(false);

    // Transcript state
    const [transcript, setTranscript] = useState([]);
    const [transcriptStatus, setTranscriptStatus] = useState('PENDING');
    const [isTranscribing, setIsTranscribing] = useState(false);

    // Summary state
    const [callSummary, setCallSummary] = useState(null);
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [showSummaryModal, setShowSummaryModal] = useState(false);

    // Call history
    const [callHistory, setCallHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // Refs for timers
    const durationTimerRef = useRef(null);
    const transcriptPollerRef = useRef(null);
    const statusPollerRef = useRef(null);

    /**
     * Start call duration timer
     */
    const startDurationTimer = useCallback(() => {
        if (durationTimerRef.current) {
            clearInterval(durationTimerRef.current);
        }
        setCallDuration(0);
        durationTimerRef.current = setInterval(() => {
            setCallDuration(prev => prev + 1);
        }, 1000);
    }, []);

    /**
     * Stop call duration timer
     */
    const stopDurationTimer = useCallback(() => {
        if (durationTimerRef.current) {
            clearInterval(durationTimerRef.current);
            durationTimerRef.current = null;
        }
    }, []);

    /**
     * Start polling for transcript updates
     */
    const startTranscriptPolling = useCallback((callId) => {
        if (transcriptPollerRef.current) {
            clearInterval(transcriptPollerRef.current);
        }

        const pollTranscript = async () => {
            try {
                const data = await getTranscript(callId);
                setTranscript(data.segments || []);
                setTranscriptStatus(data.status);
                setIsTranscribing(data.status === 'TRANSCRIBING');
            } catch (err) {
                console.error('Error polling transcript:', err);
            }
        };

        // Poll immediately, then every 2 seconds
        pollTranscript();
        transcriptPollerRef.current = setInterval(pollTranscript, 2000);
    }, []);

    /**
     * Stop polling for transcript
     */
    const stopTranscriptPolling = useCallback(() => {
        if (transcriptPollerRef.current) {
            clearInterval(transcriptPollerRef.current);
            transcriptPollerRef.current = null;
        }
    }, []);

    /**
     * Stop polling for call status
     */
    const stopStatusPolling = useCallback(() => {
        if (statusPollerRef.current) {
            clearInterval(statusPollerRef.current);
            statusPollerRef.current = null;
        }
    }, []);

    // Track if we've already connected (to avoid re-running connection logic)
    const hasConnectedRef = useRef(false);

    /**
     * Start polling for call status updates
     */
    const startStatusPolling = useCallback((callId) => {
        if (statusPollerRef.current) {
            clearInterval(statusPollerRef.current);
        }
        hasConnectedRef.current = false;

        const pollStatus = async () => {
            try {
                const data = await getCallDetails(callId);
                const backendStatus = data.status?.toLowerCase();

                // Map backend status to our status constants
                if (backendStatus === 'connected' || backendStatus === 'in-progress' || backendStatus === 'in_progress') {
                    if (!hasConnectedRef.current) {
                        hasConnectedRef.current = true;
                        setCallStatus(CALL_STATUS.CONNECTED);
                        startDurationTimer();
                        startTranscriptPolling(callId);
                    }
                } else if (backendStatus === 'completed' || backendStatus === 'ended' || backendStatus === 'no-answer' || backendStatus === 'busy' || backendStatus === 'canceled' || backendStatus === 'failed') {
                    // Call has ended
                    setCallStatus(CALL_STATUS.ENDED);
                    stopDurationTimer();
                    stopTranscriptPolling();
                    stopStatusPolling();
                    setShowSummaryModal(true);
                } else if (backendStatus === 'ringing' || backendStatus === 'queued' || backendStatus === 'initiated') {
                    setCallStatus(CALL_STATUS.CONNECTING);
                }
            } catch (err) {
                console.error('Error polling call status:', err);
            }
        };

        // Poll immediately, then every 2 seconds
        pollStatus();
        statusPollerRef.current = setInterval(pollStatus, 2000);
    }, [startDurationTimer, startTranscriptPolling, stopDurationTimer, stopTranscriptPolling, stopStatusPolling]);

    // Pending call ref - stores call info to dial after CCP is ready
    const pendingCallRef = useRef(null);

    /**
     * Execute the pending call once CCP/agent is ready
     * Note: This attempts programmatic dialing but CCP handles the actual call
     * Even if this fails, CCP may still work - don't show error to user
     */
    const executePendingCall = useCallback(() => {
        const pendingCall = pendingCallRef.current;
        if (!pendingCall) return;

        console.log('[PhoneCall] Executing pending call to:', pendingCall.phoneNumber);

        try {
            // Get the agent instance
            const agent = new window.connect.Agent();

            // Create endpoint for the phone number
            const endpoint = window.connect.Endpoint.byPhoneNumber(pendingCall.phoneNumber);

            // Initiate the call via CCP
            agent.connect(endpoint, {
                success: () => {
                    console.log('[PhoneCall] Call initiated via CCP');
                    setCallStatus(CALL_STATUS.CONNECTING);
                    // Clear pending call after successful initiation
                    pendingCallRef.current = null;
                },
                failure: (err) => {
                    // Don't show error - CCP is visible and user can dial manually
                    // The call often works via CCP even when this callback reports failure
                    console.warn('[PhoneCall] Programmatic dial reported failure, but CCP may still work:', err);
                    pendingCallRef.current = null;
                }
            });
        } catch (err) {
            // Don't show error - CCP is visible and user can dial manually
            console.warn('[PhoneCall] Programmatic dial error, but CCP is available:', err);
            pendingCallRef.current = null;
        }
    }, []);

    /**
     * Called by PhoneCallModal when CCP is ready and agent is available
     */
    const onCCPReady = useCallback(() => {
        console.log('[PhoneCall] CCP ready callback received');
        if (pendingCallRef.current) {
            executePendingCall();
        }
    }, [executePendingCall]);

    /**
     * Start a new outbound call via CCP (Connect Streams)
     * This uses the CCP softphone to dial, not the backend API
     */
    const startCall = useCallback(async ({ phoneNumber, contactName, leadId, caseId }) => {
        setError(null);
        setIsOpen(true);
        setIsMinimized(false);
        setTranscript([]);
        setCallSummary(null);

        // Normalize phone number to E.164 format
        const normalizedPhone = normalizePhoneToE164(phoneNumber);

        // Store the contact info for display
        setCurrentCall({
            phoneNumber: normalizedPhone,
            contactName,
            leadId,
            caseId,
        });

        // Store pending call info - modal will execute when CCP is ready
        pendingCallRef.current = {
            phoneNumber: normalizedPhone,
            contactName,
            leadId,
            caseId,
        };

        setCallStatus(CALL_STATUS.INITIATING);
        console.log('[PhoneCall] Pending call set, waiting for CCP ready...');
    }, []);

    /**
     * End the current call
     */
    const hangUp = useCallback(async (reason = 'COMPLETED') => {
        if (!currentCall?.callId) return;

        stopDurationTimer();
        stopTranscriptPolling();
        stopStatusPolling();

        try {
            await endCall(currentCall.callId, reason);
            setCallStatus(CALL_STATUS.ENDED);

            // Show summary modal after call ends
            setShowSummaryModal(true);
        } catch (err) {
            setError(err.message || 'Failed to end call');
            setCallStatus(CALL_STATUS.ENDED);
        }
    }, [currentCall, stopDurationTimer, stopTranscriptPolling, stopStatusPolling]);

    /**
     * Toggle mute state
     */
    const toggleMute = useCallback(() => {
        setIsMuted(prev => !prev);
        // In real implementation, this would interact with Amazon Connect
    }, []);

    /**
     * Toggle hold state
     */
    const toggleHold = useCallback(() => {
        setIsOnHold(prev => {
            const newState = !prev;
            setCallStatus(newState ? CALL_STATUS.ON_HOLD : CALL_STATUS.CONNECTED);
            return newState;
        });
        // In real implementation, this would interact with Amazon Connect
    }, []);

    /**
     * Toggle recording
     */
    const toggleRecording = useCallback(() => {
        setIsRecording(prev => !prev);
        // In real implementation, this would interact with Amazon Connect
    }, []);

    /**
     * Send DTMF tone
     */
    const sendDTMF = useCallback((digit) => {
        console.log('DTMF:', digit);
        // In real implementation, this would send DTMF through Amazon Connect
    }, []);

    /**
     * Generate AI summary of the call
     */
    const generateSummary = useCallback(async () => {
        if (!currentCall?.callId) return;

        setIsSummarizing(true);
        setError(null);

        try {
            const response = await generateCallSummary(currentCall.callId);
            setCallSummary(response.summary);
        } catch (err) {
            setError(err.message || 'Failed to generate summary');
        } finally {
            setIsSummarizing(false);
        }
    }, [currentCall]);

    /**
     * Save call summary as a case comment
     */
    const saveSummaryAsComment = useCallback(async (caseId, editedSummary) => {
        if (!currentCall?.callId) return;

        try {
            await saveCallAsComment(currentCall.callId, caseId, editedSummary);
            return true;
        } catch (err) {
            setError(err.message || 'Failed to save comment');
            return false;
        }
    }, [currentCall]);

    /**
     * Load call history for a lead or case
     */
    const loadCallHistory = useCallback(async ({ leadId, caseId }) => {
        setIsLoadingHistory(true);
        try {
            const response = await getCallHistory({ leadId, caseId });
            setCallHistory(response.calls || []);
        } catch (err) {
            console.error('Failed to load call history:', err);
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    /**
     * Close the phone modal
     */
    const closeModal = useCallback(() => {
        // Only allow closing if call is ended or idle
        if (callStatus === CALL_STATUS.IDLE || callStatus === CALL_STATUS.ENDED || callStatus === CALL_STATUS.FAILED) {
            // Stop any lingering pollers
            stopDurationTimer();
            stopTranscriptPolling();
            stopStatusPolling();

            // Clear pending call ref to prevent stale calls
            pendingCallRef.current = null;

            setIsOpen(false);
            setIsMinimized(false);
            setCurrentCall(null);
            setCallStatus(CALL_STATUS.IDLE);
            setCallDuration(0);
            setTranscript([]);
            setCallSummary(null);
            setShowSummaryModal(false);
            setIsMuted(false);
            setIsOnHold(false);
            setIsRecording(false);
            setShowKeypad(false);
            setError(null);
        }
    }, [callStatus, stopDurationTimer, stopTranscriptPolling, stopStatusPolling]);

    /**
     * Minimize/maximize the modal
     */
    const toggleMinimize = useCallback(() => {
        setIsMinimized(prev => !prev);
    }, []);

    /**
     * Open the dialer (without starting a call)
     */
    const openDialer = useCallback(() => {
        // Clear any pending call from previous click-to-call
        pendingCallRef.current = null;

        setIsOpen(true);
        setIsMinimized(false);
        setCallStatus(CALL_STATUS.IDLE);
        setCurrentCall(null);
        setError(null);
        setTranscript([]);
        setCallSummary(null);
    }, []);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            stopDurationTimer();
            stopTranscriptPolling();
            stopStatusPolling();
        };
    }, [stopDurationTimer, stopTranscriptPolling, stopStatusPolling]);

    return {
        // Modal state
        isOpen,
        isMinimized,
        setIsOpen,
        toggleMinimize,
        closeModal,

        // Call state
        callStatus,
        currentCall,
        callDuration,
        error,

        // Call actions
        startCall,
        hangUp,
        openDialer,
        onCCPReady,

        // For modal to update call state from CCP events
        setCallStatus,
        setCurrentCall,
        setError,
        startDurationTimer,
        stopDurationTimer,
        setShowSummaryModal,

        // Call controls
        isMuted,
        isOnHold,
        isRecording,
        showKeypad,
        toggleMute,
        toggleHold,
        toggleRecording,
        setShowKeypad,
        sendDTMF,

        // Transcript
        transcript,
        transcriptStatus,
        isTranscribing,

        // Summary
        callSummary,
        isSummarizing,
        showSummaryModal,
        setShowSummaryModal,
        generateSummary,
        saveSummaryAsComment,

        // History
        callHistory,
        isLoadingHistory,
        loadCallHistory
    };
}

export default usePhoneCall;
